import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import serviceApi from '../../services/serviceApi';
import { PAYMENT_METHOD_LABELS } from '../../config/serviceConstants';
import { formatSAR } from '../../utils/currencyFormatter';
import { arabicErrorFor } from '../../utils/serviceErrorMessages';

const REFUNDABLE_METHODS = ['bank_transfer', 'cash'];

// `defaultRefundedAt` is the server's business date for this operation (Riyadh) —
// see RecordPaymentModal. Named per operation on purpose: a refund could later
// default to the service date without any change here.
const RecordRefundModal = ({ show, onHide, invoice, defaultRefundedAt, onRecorded }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  const total = invoice?.commercials?.totalAmount || 0;
  const refundedSoFar = (invoice?.refunds || []).reduce((sum, r) => sum + (r.amount || 0), 0);
  const refundCap = Math.max(0, total - refundedSoFar);

  const [form, setForm] = useState({
    amount: '',
    method: 'bank_transfer',
    refundedAt: '',
    note: ''
  });

  useEffect(() => {
    if (show) {
      setForm({
        amount: refundCap ? refundCap.toFixed(2) : '',
        method: 'bank_transfer',
        refundedAt: defaultRefundedAt || '',
        note: ''
      });
      setError(null);
    }
  }, [show, refundCap, defaultRefundedAt]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    setError(null);
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) {
      setError('يرجى إدخال مبلغ صحيح');
      return;
    }
    if (amt > refundCap + 0.001) {
      setError(`المبلغ يتجاوز الحد المسموح للاسترداد (${formatSAR(refundCap)})`);
      return;
    }
    if (!form.note.trim()) {
      setError('يرجى إدخال سبب الاسترداد');
      return;
    }
    setSaving(true);
    try {
      await serviceApi.invoices.recordRefund(invoice._id, {
        amount: amt,
        method: form.method,
        refundedAt: form.refundedAt,
        note: form.note.trim()
      });
      onRecorded?.();
      onHide();
    } catch (err) {
      setError(arabicErrorFor(err, 'تعذر تسجيل الاسترداد'));
    } finally {
      setSaving(false);
    }
  };

  if (!invoice) return null;

  return (
    <Modal show={show} onHide={onHide} centered dir="rtl">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 700, color: '#b8860b' }}>
          تسجيل استرداد
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}

        <div style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: 16 }}>
          الفاتورة: <strong>{invoice.invoiceNumber}</strong>
          {' • '}
          المسترد سابقاً: <strong>{formatSAR(refundedSoFar)}</strong>
          {' • '}
          الحد المتاح: <strong>{formatSAR(refundCap)}</strong>
        </div>

        <div className="d-flex gap-3 mb-3">
          <Form.Group style={{ flex: 1 }}>
            <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>المبلغ (ر.س) *</Form.Label>
            <Form.Control
              className="touch-input"
              type="number"
              min="0"
              step="0.01"
              max={refundCap}
              value={form.amount}
              onChange={(e) => handleChange('amount', e.target.value)}
              dir="ltr"
            />
          </Form.Group>
          <Form.Group style={{ flex: 1 }}>
            <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>طريقة الاسترداد *</Form.Label>
            <Form.Select
              className="touch-input"
              value={form.method}
              onChange={(e) => handleChange('method', e.target.value)}
            >
              {REFUNDABLE_METHODS.map(m => (
                <option key={m} value={m}>{PAYMENT_METHOD_LABELS[m] || m}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>

        <Form.Group className="mb-3">
          <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>تاريخ الاسترداد *</Form.Label>
          <Form.Control
            className="touch-input"
            type="date"
            value={form.refundedAt}
            onChange={(e) => handleChange('refundedAt', e.target.value)}
            dir="ltr"
          />
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>السبب *</Form.Label>
          <Form.Control
            className="touch-input"
            as="textarea"
            rows={3}
            value={form.note}
            onChange={(e) => handleChange('note', e.target.value)}
            placeholder="مطلوب — يظهر في سجل الفاتورة"
            required
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>إلغاء</Button>
        <Button
          variant="warning"
          style={{ color: '#fff', fontWeight: 500 }}
          onClick={handleSubmit}
          disabled={saving || refundCap <= 0}
        >
          {saving ? <span className="spinner-border spinner-border-sm" /> : 'تسجيل الاسترداد'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RecordRefundModal;
